import axios from 'axios';
import { db, WeatherCacheEntry } from '../db';

/**
 * 3-hour PWA-side weather cache.
 *
 * The hourly weather cron writes one WeatherHistory record per tenant per
 * hour on the server. This module mirrors the most recent N hours of those
 * records into IndexedDB so that Field Mode spray sign-off can categorise
 * conditions even when the device is offline.
 *
 * Track: automatic_weather_tracking_20260428 / Phase 4 / Task 3.
 * Spec: "PWA caches the last 3 hours of weather data."
 */

export const WEATHER_CACHE_HOURS = 3;

interface WeatherRecentResponse {
  entries: Array<{
    timestamp: string;
    temp?: number;
    windSpeed?: number;
    precipitation?: number;
    condition?: string;
    humidity?: number;
    pressure?: number;
  }>;
  hours: number;
}

/**
 * Pulls the last WEATHER_CACHE_HOURS of weather data from the backend and
 * upserts it into the local cache, then prunes any stragglers older than the
 * window. Safe to call on init, on the online event, and on the heartbeat;
 * silently no-ops when offline and never throws (network errors are logged).
 */
export async function refreshWeatherCache(): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return;
  }

  try {
    const response = await axios.get<WeatherRecentResponse>(
      '/api/v1/tenants/me/weather/recent',
      { withCredentials: true }
    );

    const cachedAt = new Date().toISOString();
    const entries: WeatherCacheEntry[] = (response.data?.entries || []).map(e => ({
      timestamp: e.timestamp,
      temp: e.temp,
      windSpeed: e.windSpeed,
      precipitation: e.precipitation,
      condition: e.condition,
      humidity: e.humidity,
      pressure: e.pressure,
      cachedAt,
    }));

    if (entries.length > 0) {
      await db.weatherCache.bulkPut(entries);
    }

    await pruneStaleCache();

    console.log(`[WeatherCache] Refreshed cache with ${entries.length} entries`);
  } catch (err) {
    console.error('[WeatherCache] Failed to refresh weather cache:', err);
  }
}

/**
 * Returns the most recent cached weather entry, or null if the cache is empty.
 * Used by Field Mode sign-off as the offline default.
 */
export async function getCachedWeather(): Promise<WeatherCacheEntry | null> {
  const entry = await db.weatherCache.orderBy('timestamp').reverse().first();
  return entry || null;
}

/**
 * Returns all cached entries newest-first. Used by future UI that wants to
 * render the recent-hours trend line.
 */
export async function getCachedWeatherHistory(): Promise<WeatherCacheEntry[]> {
  return db.weatherCache.orderBy('timestamp').reverse().toArray();
}

/**
 * Removes entries older than WEATHER_CACHE_HOURS. Idempotent.
 */
export async function pruneStaleCache(): Promise<void> {
  const cutoff = new Date(Date.now() - WEATHER_CACHE_HOURS * 60 * 60 * 1000).toISOString();
  await db.weatherCache.where('timestamp').below(cutoff).delete();
}
